import React from 'react';
import {
  View,
} from 'react-native';
import { CartesianChart, Bar, useChartPressState } from 'victory-native';
import { useFont } from '@shopify/react-native-skia';
import Colors from '../constants/Colors';

interface ChildrenBarGraphMobileProps {
  data: { assetName: string; templateName: string; location: string; count: number }[];
  comparisonData: { assetName: string; templateName: string; location: string; count: number }[];
  compareToggled: boolean;
}

// helper to align comparison dataset
function mergeData(
  data: { assetName: string; templateName: string; location: string; count: number }[],
  comparisonData: { assetName: string; templateName: string; location: string; count: number }[],
) {
  return (data ?? []).map((item, i) => ({
    ...item,
    comparison: comparisonData?.[i]?.count ?? 0,
  }));
}

const ChildrenBarGraph = ({
  data, comparisonData, compareToggled,
}: ChildrenBarGraphMobileProps) => {
  const maxCount: number = Math.max(
    ...(data ?? []).map((item) => item.count),
    ...(comparisonData ?? []).map((item) => item?.count ?? 0),
    1,
  );

  const { state } = useChartPressState({ x: '', y: { count: 0 } });

  const width = Math.max(data?.length ?? 0, comparisonData?.length ?? 0) * 70 + 100;

  // eslint-disable-next-line global-require
  const font = useFont(require('../assets/fonts/Roboto-Regular.ttf'), 14);
  if (!font) { return null; }

  return (
    <View style={{ width, minHeight: 300 }}>
      {!compareToggled ? (
        <CartesianChart
          data={data ?? []}
          xKey="assetName"
          yKeys={['count']}
          domainPadding={{ left: 40, right: 50 }}
          domain={{ y: [0, 1.2 * maxCount] }}
          frame={{ lineWidth: 0 }}
          axisOptions={{
            font,
            tickCount: { x: (data?.length ?? 0) - 1, y: 6 },
            formatXLabel: (label: string) => (label?.length > 10 ? label.substring(0, 10) : label),
            lineColor: Colors.light.hereworksWhite200,
            lineWidth: { grid: { x: 0, y: 1 }, frame: 0 },
          }}
          chartPressState={state}
        >
          {({ points, chartBounds }) => (
            <>
              <Bar
                points={points.count.map((point) => ({
                  ...point,
                }))}
                chartBounds={chartBounds}
                color={Colors.light.hereworksDarkBlue200}
                barWidth={33}
                roundedCorners={{ topLeft: 2, topRight: 2 }}
              />
            </>
          )}
        </CartesianChart>
      ) : (
        <CartesianChart
          data={mergeData(data, comparisonData)}
          xKey="assetName"
          yKeys={['count']}
          domain={{ y: [0, 1.2 * maxCount] }}
          axisOptions={{
            font,
            tickCount: { x: (data?.length ?? 0), y: 6 },
            formatXLabel: (label: string) => (label?.length > 10 ? label.substring(0, 10) : label),
            lineColor: Colors.light.hereworksWhite100,
            lineWidth: { grid: { x: 0, y: 1 }, frame: 0 },
            labelOffset: { x: 0, y: 4 },
          }}
          padding={{
            top: 40, right: 10, bottom: 40, left: 40,
          }}
        >
          {({ points, chartBounds }) => (
            <>
              <Bar
                points={points.count}
                chartBounds={chartBounds}
                color={Colors.light.hereworksDarkBlue200}
                innerPadding={0.2}
                barWidth={14}
                roundedCorners={{ topLeft: 2, topRight: 2 }}
              />
              <Bar
                points={mergeData(data, comparisonData).map((item, idx) => ({
                  ...points.count[idx],
                  y: item.comparison,
                }))}
                chartBounds={chartBounds}
                color={Colors.light.hereworksYellow200}
                innerPadding={0.2}
                barWidth={14}
                roundedCorners={{ topLeft: 2, topRight: 2 }}
              />
            </>
          )}
        </CartesianChart>
      )}
    </View>
  );
};

export default ChildrenBarGraph;
